import React, { useCallback, useEffect, useId, useRef } from 'react';
import styles from '../../styles/contacts/ContactsWorkspace.module.css';
import { CloseIcon } from './ContactsIcons';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Modal shell for the Contacts dialogs.
 *
 * Owns the accessibility contract the rest of the feature relies on: labelled
 * `role="dialog"`, Escape to dismiss, focus moved in on open and returned to
 * the trigger on close, and Tab cycling kept inside the panel.
 */
export default function Dialog({
  open,
  onClose,
  title,
  lead,
  children,
  footer,
  wide = false,
  dismissible = true,
  initialFocusRef,
  labelledBy,
}) {
  const panelRef = useRef(null);
  const restoreFocusRef = useRef(null);
  const generatedId = useId();
  const titleId = labelledBy || `${generatedId}-title`;

  // Callers pass inline handlers, so these are read through refs: a changing
  // handler identity must not re-run the effect below, which would re-capture
  // the focus-restore target as an element inside the open dialog.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const dismissibleRef = useRef(dismissible);
  dismissibleRef.current = dismissible;

  const requestClose = useCallback(() => {
    if (dismissibleRef.current) onCloseRef.current?.();
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    restoreFocusRef.current = document.activeElement;
    const panel = panelRef.current;
    const target =
      initialFocusRef?.current || panel?.querySelector(FOCUSABLE) || panel;
    // Wait a frame so the entry animation does not fight the scroll-into-view
    // that focusing inside a freshly mounted panel can trigger.
    const raf = requestAnimationFrame(() => target?.focus?.({ preventScroll: true }));

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        requestClose();
        return;
      }
      if (event.key !== 'Tab' || !panelRef.current) return;
      const items = Array.from(panelRef.current.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKeyDown, true);
      const restore = restoreFocusRef.current;
      if (restore && typeof restore.focus === 'function' && document.contains(restore)) {
        restore.focus({ preventScroll: true });
      }
    };
  }, [open, initialFocusRef, requestClose]);

  if (!open) return null;

  return (
    <div
      className={styles.overlay}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <div
        ref={panelRef}
        className={[styles.dialog, wide ? styles.dialogWide : ''].filter(Boolean).join(' ')}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <div className={styles.dialogHead}>
          <h2 className={styles.dialogTitle} id={titleId}>
            {title}
          </h2>
          {dismissible ? (
            <button type="button" className={styles.iconBtn} onClick={requestClose} aria-label="Close dialog">
              <CloseIcon />
            </button>
          ) : null}
        </div>
        {lead ? <p className={styles.dialogLead}>{lead}</p> : null}
        {children ? <div className={styles.dialogBody}>{children}</div> : null}
        {footer}
      </div>
    </div>
  );
}
